/**
 * Validate asset versions, changelogs, and manifest schemas across asset repos.
 */

import { execSync } from "node:child_process";
import fs from "node:fs";
import path from "node:path";
import {
  ASSET_NAME_HINT,
  checkAgentSkillConformance,
  isValidAssetName,
  parseAssetRef,
} from "@skaile/workspaces/core";
import type { IAssetKindRegistry } from "@skaile/workspaces/plugins";
import { parseSkillFrontmatter, validateFlowVersions } from "@skaile/workspaces/resolver";
import { Command } from "commander";
import { parse as parseYaml } from "yaml";
import { logErr, logInfo, logOk, logWarn } from "../helpers.ts";
import { createFullRegistry } from "../open-registry.ts";
import { AI_RESOURCES } from "../paths.ts";

// ---------------------------------------------------------------------------
// Manifest schema validation (skaile validate [path])
// ---------------------------------------------------------------------------

/** Per-type regex patterns for deterministic asset discovery. */
const MANIFEST_GLOBS: Array<{ pattern: RegExp; kind: string }> = [
  { pattern: /\/SKILL\.md$/, kind: "skill" },
  { pattern: /\/agent\.yaml$/, kind: "agent" },
  { pattern: /\/AGENT\.md$/, kind: "agent" },
  { pattern: /\/CONTRACT\.md$/, kind: "contract" },
  { pattern: /\.flow\.yaml$/, kind: "flow" },
  { pattern: /\.flow\.json$/, kind: "flow" },
  { pattern: /\/CONNECTOR\.md$/, kind: "connector" },
  { pattern: /\/MOUNT\.md$/, kind: "mount" },
  { pattern: /\.prompt\.md$/, kind: "prompt" },
  { pattern: /\/MCP\.md$/, kind: "mcp-server" },
  { pattern: /\/knowledge\.yaml$/, kind: "knowledge" },
  { pattern: /\/knowledge\/index\.yaml$/, kind: "knowledge" },
  { pattern: /\.preset\.yaml$/, kind: "preset" },
];

/** Directories to skip during tree walk. */
const SKIP_DIRS = new Set(["node_modules", "dist", ".git", ".skaile", "__pycache__"]);

interface ManifestCandidate {
  filePath: string;
  kind: string;
}

/** Recursively walk a directory tree and find manifest candidates. */
function findManifestCandidates(rootPath: string): ManifestCandidate[] {
  const candidates: ManifestCandidate[] = [];

  function walk(dir: string): void {
    let entries: fs.Dirent[];
    try {
      entries = fs.readdirSync(dir, { withFileTypes: true });
    } catch {
      return;
    }
    for (const entry of entries) {
      const fullPath = path.join(dir, entry.name);
      if (entry.isDirectory()) {
        if (!SKIP_DIRS.has(entry.name)) walk(fullPath);
      } else if (entry.isFile()) {
        for (const glob of MANIFEST_GLOBS) {
          if (glob.pattern.test(fullPath)) {
            candidates.push({ filePath: fullPath, kind: glob.kind });
            break;
          }
        }
      }
    }
  }

  walk(rootPath);
  return candidates;
}

/** Parse frontmatter from a markdown file (SKILL.md, CONTRACT.md, etc.) */
function parseMdFrontmatter(content: string): unknown {
  const fmMatch = content.match(/^---\n([\s\S]*?)\n---/);
  if (!fmMatch) return null;
  return parseYaml(fmMatch[1]);
}

/**
 * Check an asset manifest's canonical identity (the name/id that becomes its
 * asset ref). Flows carry identity in `id`; every other kind uses `name`.
 * When a `publisher` is present we round-trip through `parseAssetRef` so the
 * full canonical grammar (publisher shape + name) is exercised, which is the
 * exact path that hard-errors at install/apply time. Returns an error message,
 * or `null` when the identity is absent (required-ness is the schema's job) or
 * valid.
 */
function checkManifestIdentity(kind: string, parsed: unknown): string | null {
  if (parsed === null || typeof parsed !== "object") return null;
  const obj = parsed as Record<string, unknown>;
  const identity = kind === "flow" ? obj.id : obj.name;
  if (typeof identity !== "string" || identity.length === 0) return null;

  const publisher = typeof obj.publisher === "string" ? obj.publisher : undefined;
  if (publisher !== undefined) {
    try {
      parseAssetRef(`${kind}:@${publisher}/${identity}`);
      return null;
    } catch (e) {
      return e instanceof Error ? e.message : String(e);
    }
  }
  if (!isValidAssetName(identity)) {
    return `invalid asset ${kind === "flow" ? "id" : "name"} "${identity}" — ${ASSET_NAME_HINT}`;
  }
  return null;
}

/**
 * Parse a single manifest candidate's bytes. Returns the parsed value, or a
 * sentinel: `"skip"` (MD without frontmatter — not an error) or `"error"`
 * (parse failure already logged, count it).
 */
function parseManifestCandidate(
  c: ManifestCandidate,
  absRoot: string,
): { kind: "value"; parsed: unknown } | { kind: "skip" } | { kind: "error" } {
  const content = fs.readFileSync(c.filePath, "utf-8");

  // Parse based on file type
  if (c.filePath.endsWith(".md")) {
    const parsed = parseMdFrontmatter(content);
    if (parsed === null) {
      // MD files without frontmatter are not necessarily errors
      // (could be pure prose contracts)
      return { kind: "skip" };
    }
    return { kind: "value", parsed };
  }

  // YAML/JSON files
  try {
    const parsed = c.filePath.endsWith(".json") ? JSON.parse(content) : parseYaml(content);
    return { kind: "value", parsed };
  } catch (e) {
    const relPath = path.relative(absRoot, c.filePath);
    logErr(`${relPath} — parse error: ${e instanceof Error ? e.message : String(e)}`);
    return { kind: "error" };
  }
}

/**
 * Validate a single parsed manifest candidate against identity rules + the kind
 * registry. Returns the error/warning deltas this candidate contributes.
 */
function validateManifestCandidate(
  c: ManifestCandidate,
  parsed: unknown,
  absRoot: string,
  registry: IAssetKindRegistry,
  strictConformance: boolean,
): { errors: number; warnings: number } {
  const relPath = path.relative(absRoot, c.filePath);
  let errors = 0;
  let warnings = 0;

  // Canonical identity (name/id) compliance — runs for every kind, registered
  // or not. Catches legacy names like "CLI Concept" before they hard-error at
  // install/apply time inside parseAssetRef.
  const identityError = checkManifestIdentity(c.kind, parsed);
  if (identityError) {
    logErr(`${relPath} [${c.kind}] — ${identityError}`);
    errors++;
  }

  // Agent Skills (agentskills.io) standard conformance — skills only. Advisory
  // warnings by default (exit code unchanged); --strict-conformance escalates
  // every finding to an error.
  if (c.kind === "skill" && parsed && typeof parsed === "object") {
    const dirName = path.basename(path.dirname(c.filePath)); // SKILL.md's folder
    const findings = checkAgentSkillConformance(parsed as Record<string, unknown>, { dirName });
    for (const f of findings) {
      const line = `${relPath} [skill conformance] — ${f.field}: ${f.message}`;
      if (strictConformance) {
        logErr(line);
        errors++;
      } else {
        logWarn(line);
        warnings++;
      }
    }
  }

  // Route validation through the registry (A5: unregistered kinds get a WARN)
  const provider = registry.getProvider(c.kind);

  if (!provider) {
    logWarn(`${relPath} [${c.kind}] — kind "${c.kind}" is not registered, skipping validation`);
    return { errors, warnings: warnings + 1 };
  }

  const result = provider.validateManifest(parsed);
  if (!result.ok) {
    for (const err of result.errors) {
      logErr(`${relPath} [${c.kind}] — ${err.path ? `${err.path}: ` : ""}${err.message}`);
    }
    errors += result.errors.length;
  }

  return { errors, warnings };
}

/** Validate all manifest files in a directory tree using the kind registry. */
function validateManifests(
  rootPath: string,
  strictConformance: boolean,
): { total: number; errors: number; warnings: number } {
  const absRoot = path.resolve(rootPath);
  if (!fs.existsSync(absRoot)) {
    logErr(`Path does not exist: ${absRoot}`);
    return { total: 0, errors: 1, warnings: 0 };
  }

  const registry: IAssetKindRegistry = createFullRegistry();
  const candidates = findManifestCandidates(absRoot);
  let errors = 0;
  let warnings = 0;

  for (const c of candidates) {
    const parseResult = parseManifestCandidate(c, absRoot);
    if (parseResult.kind === "skip") continue;
    if (parseResult.kind === "error") {
      errors++;
      continue;
    }

    const delta = validateManifestCandidate(
      c,
      parseResult.parsed,
      absRoot,
      registry,
      strictConformance,
    );
    errors += delta.errors;
    warnings += delta.warnings;
  }

  return { total: candidates.length, errors, warnings };
}

/** Recursively find every `skaile.yaml` / `*.skaile.yaml` workspace manifest. */
function findWorkspaceManifests(rootPath: string): string[] {
  const out: string[] = [];
  function walk(dir: string): void {
    let entries: fs.Dirent[];
    try {
      entries = fs.readdirSync(dir, { withFileTypes: true });
    } catch {
      return;
    }
    for (const entry of entries) {
      const full = path.join(dir, entry.name);
      if (entry.isDirectory()) {
        if (!SKIP_DIRS.has(entry.name)) walk(full);
      } else if (entry.isFile() && /(^|\.)skaile\.yaml$/.test(entry.name)) {
        out.push(full);
      }
    }
  }
  walk(rootPath);
  return out;
}

/**
 * Validate the dependency refs and asset identities declared in every
 * `skaile.yaml` under a tree. This is the manifest-level mirror of the
 * per-file identity check: it parses each `dependencies[]` /
 * `overrides[].ref` through `parseAssetRef` (the exact code path that produced
 * the original "publisher required …" / "invalid asset name …" hard error) and
 * checks each `assets[].name` against the canonical name grammar.
 */
/**
 * Collect every `[field, ref]` pair to validate from a workspace manifest:
 * each string in `dependencies[]` and each `overrides[].ref`.
 */
function collectWorkspaceRefFields(parsed: Record<string, unknown>): Array<[string, string]> {
  const refFields: Array<[string, string]> = [];
  if (Array.isArray(parsed.dependencies)) {
    for (const d of parsed.dependencies) {
      if (typeof d === "string") refFields.push(["dependencies", d]);
    }
  }
  if (Array.isArray(parsed.overrides)) {
    for (const o of parsed.overrides) {
      const ref = (o as Record<string, unknown>)?.ref;
      if (typeof ref === "string") refFields.push(["overrides.ref", ref]);
    }
  }
  return refFields;
}

/** Parse each declared ref through `parseAssetRef`; returns the error count. */
function validateWorkspaceRefs(refFields: Array<[string, string]>, relPath: string): number {
  let errors = 0;
  for (const [field, ref] of refFields) {
    try {
      parseAssetRef(ref);
    } catch (e) {
      logErr(`${relPath} [${field}] — ${e instanceof Error ? e.message : String(e)}`);
      errors++;
    }
  }
  return errors;
}

/** Validate `assets[].name` against the canonical name/ref grammar. */
function validateWorkspaceAssets(parsed: Record<string, unknown>, relPath: string): number {
  if (!Array.isArray(parsed.assets)) return 0;
  let errors = 0;
  const topPublisher = typeof parsed.publisher === "string" ? parsed.publisher : undefined;
  for (const a of parsed.assets) {
    const asset = a as Record<string, unknown>;
    const name = asset?.name;
    const kind = typeof asset?.kind === "string" ? asset.kind : "skill";
    if (typeof name !== "string" || name.length === 0) continue;
    const publisher = typeof asset?.publisher === "string" ? asset.publisher : topPublisher;
    if (publisher !== undefined) {
      try {
        parseAssetRef(`${kind}:@${publisher}/${name}`);
      } catch (e) {
        logErr(`${relPath} [assets] — ${e instanceof Error ? e.message : String(e)}`);
        errors++;
      }
    } else if (!isValidAssetName(name)) {
      logErr(`${relPath} [assets] — invalid asset name "${name}" — ${ASSET_NAME_HINT}`);
      errors++;
    }
  }
  return errors;
}

function validateWorkspaceManifests(rootPath: string): {
  total: number;
  errors: number;
} {
  const absRoot = path.resolve(rootPath);
  const files = findWorkspaceManifests(absRoot);
  let errors = 0;

  for (const file of files) {
    const relPath = path.relative(absRoot, file);
    let parsed: Record<string, unknown>;
    try {
      parsed = (parseYaml(fs.readFileSync(file, "utf-8")) ?? {}) as Record<string, unknown>;
    } catch (e) {
      logErr(`${relPath} — parse error: ${e instanceof Error ? e.message : String(e)}`);
      errors++;
      continue;
    }

    errors += validateWorkspaceRefs(collectWorkspaceRefFields(parsed), relPath);
    errors += validateWorkspaceAssets(parsed, relPath);
  }

  return { total: files.length, errors };
}

/** Domains excluded from version/changelog checks */
const EXCLUDED_DOMAINS = new Set(["skaileup-shared", "external", "docs"]);

interface AssetVersion {
  kind: "skill" | "agent" | "flow" | "prompt";
  name: string;
  domain: string;
  version: string;
  filePath: string;
}

/** Walk ai-assets and collect all asset versions */
function collectAssetVersions(): AssetVersion[] {
  const assets: AssetVersion[] = [];
  if (!fs.existsSync(AI_RESOURCES)) return assets;

  const domains = fs
    .readdirSync(AI_RESOURCES, { withFileTypes: true })
    .filter((d) => d.isDirectory() && !EXCLUDED_DOMAINS.has(d.name));

  for (const d of domains) {
    const domainDir = path.join(AI_RESOURCES, d.name);

    // Skills
    const skillsDir = path.join(domainDir, "skills");
    if (fs.existsSync(skillsDir)) {
      collectSkillVersions(skillsDir, d.name, assets);
    }

    // Agents
    const agentsDir = path.join(domainDir, "agents");
    if (fs.existsSync(agentsDir)) {
      collectAgentVersions(agentsDir, d.name, assets);
    }

    // Flows
    const flowsDir = path.join(domainDir, "flows");
    if (fs.existsSync(flowsDir)) {
      collectFlowVersions(flowsDir, d.name, assets);
    }

    // Prompts
    const promptsDir = path.join(domainDir, "prompts");
    if (fs.existsSync(promptsDir)) {
      collectPromptVersions(promptsDir, d.name, assets);
    }
  }

  return assets;
}

function collectFlowVersions(dir: string, domain: string, out: AssetVersion[]): void {
  for (const f of fs.readdirSync(dir)) {
    if (!f.endsWith(".flow.yaml")) continue;
    const fp = path.join(dir, f);
    const content = fs.readFileSync(fp, "utf-8");
    const parsed = parseYaml(content);
    out.push({
      kind: "flow",
      name: parsed?.id ?? f.replace(".flow.yaml", ""),
      domain,
      version: parsed?.version ?? "",
      filePath: fp,
    });
  }
}

function collectPromptVersions(dir: string, domain: string, out: AssetVersion[]): void {
  for (const f of fs.readdirSync(dir)) {
    if (!f.endsWith(".prompt.md")) continue;
    const fp = path.join(dir, f);
    const content = fs.readFileSync(fp, "utf-8");
    const fm = parseSkillFrontmatter(content);
    out.push({
      kind: "prompt",
      name: fm?.name ?? f.replace(".prompt.md", ""),
      domain,
      version: fm?.metadata?.version ?? "",
      filePath: fp,
    });
  }
}

function collectSkillVersions(dir: string, domain: string, out: AssetVersion[]): void {
  for (const entry of fs.readdirSync(dir, { withFileTypes: true })) {
    if (!entry.isDirectory()) continue;
    const full = path.join(dir, entry.name);
    const skillMd = path.join(full, "SKILL.md");
    if (fs.existsSync(skillMd)) {
      const content = fs.readFileSync(skillMd, "utf-8");
      const fm = parseSkillFrontmatter(content);
      out.push({
        kind: "skill",
        name: fm?.name ?? entry.name,
        domain,
        version: fm?.metadata?.version ?? "",
        filePath: skillMd,
      });
    } else {
      collectSkillVersions(full, domain, out);
    }
  }
}

function collectAgentVersions(dir: string, domain: string, out: AssetVersion[]): void {
  for (const entry of fs.readdirSync(dir, { withFileTypes: true })) {
    if (!entry.isDirectory()) continue;
    const agentYaml = path.join(dir, entry.name, "agent.yaml");
    if (!fs.existsSync(agentYaml)) continue;
    const content = fs.readFileSync(agentYaml, "utf-8");
    const parsed = parseYaml(content);
    out.push({
      kind: "agent",
      name: parsed?.name ?? entry.name,
      domain,
      version: parsed?.version ?? "",
      filePath: agentYaml,
    });
  }
}

/** Check if a version string is valid 3-part semver */
function isValidSemver(v: string): boolean {
  return /^\d+\.\d+\.\d+(-[\w.]+)?(\+[\w.]+)?$/.test(v);
}

/** Extract version from file content using proper YAML parsing */
function extractVersionFromContent(filePath: string, content: string): string | null {
  if (filePath.endsWith(".yaml")) {
    const parsed = parseYaml(content);
    return parsed?.version ?? null;
  }
  // SKILL.md / .prompt.md — YAML frontmatter
  const fmMatch = content.match(/^---\n([\s\S]*?)\n---/);
  if (!fmMatch) return null;
  const parsed = parseYaml(fmMatch[1]);
  return parsed?.metadata?.version ?? parsed?.version ?? null;
}

/** Filter assets down to those changed since HEAD (`git diff --name-only`). */
function filterToChangedAssets(assets: AssetVersion[]): AssetVersion[] {
  try {
    const diffOutput = execSync("git diff --name-only HEAD", {
      cwd: AI_RESOURCES,
      encoding: "utf-8",
    });
    const changedFiles = new Set(
      diffOutput
        .trim()
        .split("\n")
        .filter(Boolean)
        .map((f) => path.resolve(AI_RESOURCES, f)),
    );
    return assets.filter((a) => changedFiles.has(a.filePath));
  } catch {
    logErr("Failed to run git diff — are you in a git repository?");
    process.exit(1);
  }
}

/** Check every asset has a present + semver-valid version. */
function checkAssetVersions(assets: AssetVersion[]): { errors: number; warnings: number } {
  let errors = 0;
  let warnings = 0;
  for (const a of assets) {
    if (!a.version) {
      logErr(`${a.kind}:${a.name} (${a.domain}) — missing version`);
      errors++;
    } else if (!isValidSemver(a.version)) {
      logWarn(`${a.kind}:${a.name} (${a.domain}) — version "${a.version}" is not valid semver`);
      warnings++;
    }
  }
  return { errors, warnings };
}

/** In --changed-only mode, warn when a changed asset's version was not bumped. */
function checkVersionBumps(assets: AssetVersion[]): number {
  let warnings = 0;
  for (const a of assets) {
    if (!a.version) continue; // already flagged above
    const relPath = path.relative(AI_RESOURCES, a.filePath);
    try {
      const headContent = execSync(`git show HEAD:${relPath}`, {
        cwd: AI_RESOURCES,
        encoding: "utf-8",
      });
      const headVersion = extractVersionFromContent(a.filePath, headContent);
      if (headVersion && headVersion === a.version) {
        logWarn(
          `${a.kind}:${a.name} (${a.domain}) — file changed but version not bumped (${a.version})`,
        );
        warnings++;
      }
    } catch {
      // New file (no HEAD version) — that's fine
    }
  }
  return warnings;
}

/** Validate flow node skill-version pins against the discovered skill versions. */
function checkFlowVersionPins(assets: AssetVersion[]): number {
  const flows = assets.filter((a) => a.kind === "flow");
  const skillVersionMap = new Map<string, string>();
  for (const a of assets.filter((a) => a.kind === "skill")) {
    skillVersionMap.set(a.name, a.version);
  }

  let errors = 0;
  for (const f of flows) {
    const content = fs.readFileSync(f.filePath, "utf-8");
    const parsed = parseYaml(content);
    const nodes = (parsed?.nodes ?? [])
      .filter((n: any) => n.type === "skill" && n.data?.version)
      .map((n: any) => ({
        nodeId: n.id,
        skillId: n.data?.skill ?? n.id,
        requestedVersion: n.data?.version,
      }));

    if (nodes.length === 0) continue;
    const checks = validateFlowVersions(nodes, skillVersionMap);
    for (const c of checks.filter((c) => !c.satisfied)) {
      logErr(
        `flow:${f.name} node ${c.nodeId} — wants ${c.requested}, found ${c.actual || "(none)"}`,
      );
      errors++;
    }
  }
  return errors;
}

/** Body of `skaile validate versions` — semver + bump + flow-pin checks. */
function runVersionsCheck(opts: {
  domain?: string;
  changedOnly?: boolean;
  strict?: boolean;
}): void {
  let assets = collectAssetVersions().filter((a) => !opts.domain || a.domain === opts.domain);

  // Filter to changed files only if requested
  if (opts.changedOnly) {
    assets = filterToChangedAssets(assets);
  }

  const versionCheck = checkAssetVersions(assets);
  let errors = versionCheck.errors;
  let warnings = versionCheck.warnings;

  // In --changed-only mode, check if version was actually bumped
  if (opts.changedOnly) {
    warnings += checkVersionBumps(assets);
  }

  errors += checkFlowVersionPins(assets);

  // Summary
  console.log();
  logInfo(`Checked ${assets.length} assets`);
  if (errors === 0 && warnings === 0) {
    logOk("All versions valid");
  } else {
    if (errors > 0) logErr(`${errors} error(s)`);
    if (warnings > 0) logWarn(`${warnings} warning(s)`);
  }

  if (errors > 0 || (opts.strict && warnings > 0)) {
    process.exit(1);
  }
}

/**
 * Creates the `skaile validate` command group.
 *
 * Validates asset manifests, version fields, and changelogs in an asset
 * repository tree. Subcommands: default `validate [path]` (schema check),
 * `versions` (semver + flow pin validation), `changelog` (modified domains
 * must have updated CHANGELOG.md).
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/validate#make-validate-command
 */
export function makeValidateCommand(): Command {
  const cmd = new Command("validate").description(
    "Validate asset manifests, versions, and changelogs",
  );

  // Default action: skaile validate [path]
  cmd
    .argument("[path]", "Path to asset repo or directory to validate", ".")
    .option(
      "--strict-conformance",
      "Treat Agent Skills (agentskills.io) standard findings as errors",
    )
    .action((targetPath: string, opts: { strictConformance?: boolean }) => {
      const absPath = path.resolve(targetPath);
      logInfo(`Validating manifests in ${absPath}`);
      const { total, errors, warnings } = validateManifests(
        absPath,
        opts.strictConformance ?? false,
      );
      const ws = validateWorkspaceManifests(absPath);
      const totalErrors = errors + ws.errors;
      console.log();
      logInfo(`Scanned ${total} asset manifest(s) and ${ws.total} skaile.yaml file(s)`);
      if (warnings > 0) logWarn(`${warnings} conformance warning(s)`);
      if (totalErrors === 0) {
        logOk("All manifests valid");
      } else {
        logErr(`${totalErrors} validation error(s)`);
        process.exit(1);
      }
    });

  cmd
    .command("versions")
    .description("Check version fields and flow version pins")
    .option("--domain <name>", "Scope to one domain")
    .option("--changed-only", "Only check assets modified since last commit")
    .option("--strict", "Exit 1 on warnings (for CI)")
    .action((opts: { domain?: string; changedOnly?: boolean; strict?: boolean }) => {
      runVersionsCheck(opts);
    });

  cmd
    .command("changelog")
    .description("Check that modified domains have updated changelogs")
    .option("--base <ref>", "Git base ref for diff", "HEAD")
    .action((opts: { base: string }) => {
      let diffOutput: string;
      try {
        diffOutput = execSync(`git diff --name-only ${opts.base}`, {
          cwd: AI_RESOURCES,
          encoding: "utf-8",
        });
      } catch {
        logErr("Failed to run git diff — are you in a git repository?");
        process.exit(1);
        return;
      }

      const modifiedFiles = diffOutput.trim().split("\n").filter(Boolean);
      const assetPattern = /(SKILL\.md|agent\.yaml|\.flow\.yaml|\.prompt\.md)$/;
      const modifiedAssets = modifiedFiles.filter((f) => assetPattern.test(f));

      const modifiedDomains = new Set<string>();
      for (const f of modifiedAssets) {
        const parts = f.split("/");
        if (parts.length >= 1 && !EXCLUDED_DOMAINS.has(parts[0])) {
          modifiedDomains.add(parts[0]);
        }
      }

      if (modifiedDomains.size === 0) {
        logOk("No modified assets found");
        return;
      }

      let errors = 0;
      for (const domain of modifiedDomains) {
        const changelogModified = modifiedFiles.some(
          (f) => f === `${domain}/CHANGELOG.md` || f === path.join(domain, "CHANGELOG.md"),
        );
        if (changelogModified) {
          logOk(`${domain}/CHANGELOG.md updated`);
        } else {
          logErr(`${domain}/CHANGELOG.md not updated (assets were modified)`);
          errors++;
        }
      }

      if (errors > 0) process.exit(1);
    });

  return cmd;
}
